import { Calendar, Footer } from '@pcln/date-picker'
import { addMonths } from 'date-fns/addMonths'
import { format } from 'date-fns/format'
import { parse } from 'date-fns/parse'
import debounce from 'lodash/debounce'
import { getPaletteColor } from 'pcln-design-system'
import React from 'react'
import styled from 'styled-components'
import { themeGet } from '@styled-system/theme-get'
import { CustomDayWithPrice } from '..'
import CustomDayWithColorCode from './CustomDayWithColorCode/CustomDayWithColorCode'
import CustomFooterWithColorCode from './CustomFooterWithColorCode/CustomFooterWithColorCode'
import { getPricingData } from './getPricingData/getPricingData'
import { pricePromise, processRecords } from './helper/helper'

const StyledCalendar = styled(Calendar)`
  border-radius: ${themeGet('borderRadii.2xl')};
  &&& {
    ${(props) => `background-color: ${getPaletteColor('background.lightest')(props)}`};
  }
`

const getLowestPrice = (priceMap) => Math.min(...Object.keys(priceMap).map((k) => priceMap[k].price))

const flightsPopulatedCheck = (flightsObj) => {
  const flights = flightsObj?.flights?.[0]
  return !!(flights?.departureAirport && flights?.arrivalAirport)
}

const getFooter = (onDismiss, useColorCode, lowestPrice, flightDetails, roundedCorners) => {
  const showLegend = flightsPopulatedCheck(flightDetails)

  return useColorCode ? (
    <CustomFooterWithColorCode
      roundedCorners={roundedCorners}
      dismissCalendar={onDismiss}
      lowestPrice={lowestPrice}
      showLegend={showLegend}
    />
  ) : (
    <Footer roundedCorners={roundedCorners} dismissCalendar={onDismiss} />
  )
}

/** AirPricedCalendar */
export class AirPricedCalendar extends React.Component {
  static defaultProps = {
    dayMap: {},
    variantName: 'DEFAULT',
    flightDetails: {},
    pricePromise,
    doubleCalendarPricing: false,
  }

  constructor(props) {
    super(props)
    this.state = {
      dayMap: this.props.dayMap,
      lowestPrice: getLowestPrice(this.props.dayMap),
      useColorCode: this.props.useColorCode,
    }
    this.calendarProps = { ...this.props }

    // Delete props that are not passed to the date picker
    delete this.calendarProps.dayMap
    delete this.calendarProps.variantName
    delete this.calendarProps.flightDetails
    delete this.calendarProps.pricePromise
    delete this.calendarProps.onChange
    delete this.useColorCode

    this.debouncedPricing = debounce(this.getPricingData, 250, {
      trailing: true,
    })
    this.executedMonths = {}
    this.departDate = null
    const yearMonth = this.getYearMonth(props.startDate)
    this.displayMonths = {
      displayedFromMonth: yearMonth,
      displayedToMonth: yearMonth,
    }
  }

  componentWillUnmount() {
    this._isMounted = false
    this.debouncedPricing.cancel()
    this.calendarComponentRef = null
  }

  getYearMonth(date) {
    return date?.slice(0, 6) ?? ''
  }

  // componentDidMount implemented for
  // AIR-29152: AIR_HOME_CALENDAR_COLOR_RANGES
  componentDidMount() {
    // reusing useColorCode as it's already a boolean
    // check for AIR_HOME_CALENDAR_COLOR_RANGES variant
    const { useColorCode } = this.state

    if (this.calendarComponentRef && useColorCode && window?.innerHeight < 900) {
      this.calendarComponentRef.scrollIntoView({
        block: 'center',
      })
    }
    this.debouncedPricing(this.displayMonths)
    this._isMounted = true
  }

  onChange = (event, dates) => {
    this.departDate = dates.startDate || ''

    if (!dates.endDate) {
      this.executedMonths = {}
      const yearMonth = this.getYearMonth(dates.startDate)
      this.getPricingData(
        {
          displayedFromMonth: yearMonth,
          displayedToMonth: yearMonth,
        },
        true,
      )
    }

    if (typeof this.props.onChange === 'function') {
      this.props.onChange(event, dates)
    }
  }

  getPricingData = (displayMonths, clearDayMap) => {
    this.displayMonths = displayMonths

    const { flightDetails, pricePromise, variantName, getGraphURL, apolloClientInfo } = this.props

    getPricingData(
      displayMonths,
      flightDetails,
      pricePromise,
      variantName,
      this.executedMonths,
      this.departDate,
      getGraphURL,
      apolloClientInfo,
    )
      .then((apiRecords) => {
        if (!apiRecords) return
        if (!this._isMounted) return

        const dayMap = processRecords(apiRecords, this.calendarProps.doubleCalendarPricing)

        const newState = {
          dayMap: clearDayMap ? { ...dayMap } : { ...dayMap, ...this.state.dayMap },
        }

        newState.lowestPrice = getLowestPrice(newState.dayMap)
        this.setState(newState)
      })
      .catch(() => {
        const { displayedFromMonth, displayedToMonth } = displayMonths
        let fromMonth = parse(displayedFromMonth, 'yyyyMM', new Date())
        const toMonth = parse(displayedToMonth, 'yyyyMM', new Date())
        while (fromMonth.valueOf() <= toMonth.valueOf()) {
          const fromMonthKey = format(fromMonth, 'yyyyMM')
          delete this.executedMonths[fromMonthKey]
          fromMonth = addMonths(fromMonth, 1)
        }
      })
  }

  renderDayFn = (props) => {
    const { day, isValid, isSelected, customDayProps, isGrouped, use2024DesignSeti } = props
    const CustomDay = this.state.useColorCode ? CustomDayWithColorCode : CustomDayWithPrice

    const showColor = flightsPopulatedCheck(this.props.flightDetails)

    return (
      <CustomDay
        day={day}
        isValid={isValid}
        isSelected={isSelected}
        customDayProps={customDayProps}
        isGrouped={isGrouped}
        lowestPrice={this.state.lowestPrice}
        showColor={showColor}
        use2024DesignSeti={use2024DesignSeti}
      />
    )
  }

  onDisplayMonthsChange = (displayMonths) => {
    this.debouncedPricing(displayMonths)
  }

  render() {
    const { dayMap, useColorCode } = this.state
    const { roundedCorners, use2024DesignSeti } = this.props

    return (
      <StyledCalendar
        isFlightCalendar
        roundedCorners={roundedCorners}
        setCalendarRef={(el) => {
          this.calendarComponentRef = el
        }}
        isMobile={this.props.isMobile}
        dayGap={useColorCode ? 1 : '4px 0'}
        dayMap={dayMap}
        renderDayFn={this.renderDayFn}
        startDate={this.departDate}
        onDisplayMonthsChange={this.onDisplayMonthsChange}
        onChange={this.onChange}
        dayWithRoundedCorners
        use2024DesignSeti={use2024DesignSeti}
        footer={
          (this.props.withFooter || useColorCode) &&
          getFooter(
            this.props.onDismiss,
            this.state.useColorCode,
            this.state.lowestPrice,
            this.props.flightDetails,
            roundedCorners,
          )
        }
        {...this.calendarProps}
      />
    )
  }
}

export default AirPricedCalendar
